import type { TFunction } from 'i18next';
import React, { useEffect, useState } from 'react';
import { Image } from 'react-native';

import CalendarArtwork from '../../../../assets/dailyMissions/calendar.png';
import CardsArtwork from '../../../../assets/dailyMissions/cards.svg';
import CoinsArtwork from '../../../../assets/dailyMissions/coins.png';
import FriendsArtwork from '../../../../assets/dailyMissions/friends.svg';
import SlotsArtwork from '../../../../assets/dailyMissions/slots.svg';
import type {
	SvaraDailyMissionDto,
	SvaraMissionEventType,
	SvaraMissionRewardDto,
} from '../../../../services/api';
import { getFileStorageUrl } from '../../../../utils/axiosInstance';
import type { DailyMission } from './DailyMissionRow';

export const groupThousands = (value: number) =>
	String(Math.round(value)).replace(/\B(?=(\d{3})+(?!\d))/g, ',');

const rasterArtwork = (source: any) => (size: number) => (
	<Image source={source} style={{ width: (size * 32) / 36, height: size }} resizeMode='contain' />
);

const vectorArtwork = (Artwork: any) => (size: number) => <Artwork width={size} height={size} />;

const cardsArtwork = vectorArtwork(CardsArtwork);

const ARTWORK: Record<SvaraMissionEventType, (size: number) => React.ReactNode> = {
	HandPlayed: cardsArtwork,
	HandWon: cardsArtwork,
	GameWon: cardsArtwork,
	PotWon: cardsArtwork,
	SvarkaPlayed: cardsArtwork,
	SvarkaWon: cardsArtwork,
	HandCombination: cardsArtwork,
	ChipsEarned: rasterArtwork(CoinsArtwork),
	PointsScored: rasterArtwork(CoinsArtwork),
	BetPlaced: rasterArtwork(CoinsArtwork),
	Raise: rasterArtwork(CoinsArtwork),
	Call: rasterArtwork(CoinsArtwork),
	BlindBet: rasterArtwork(CoinsArtwork),
	DailyLogin: rasterArtwork(CalendarArtwork),
	GiftSent: vectorArtwork(FriendsArtwork),
	DuelPlayed: vectorArtwork(FriendsArtwork),
	DuelWon: vectorArtwork(FriendsArtwork),
	MiniGamePlayed: vectorArtwork(SlotsArtwork),
};

type ThumbnailArtworkProps = {
	fileId: string;
	size: number;
	renderFallback: (size: number) => React.ReactNode;
};

const ThumbnailArtwork = ({ fileId, size, renderFallback }: ThumbnailArtworkProps) => {
	const [failed, setFailed] = useState(false);

	useEffect(() => {
		setFailed(false);
	}, [fileId]);

	if (failed) return <>{renderFallback(size)}</>;

	return (
		<Image
			source={{ uri: getFileStorageUrl(fileId) }}
			style={{ width: size, height: size }}
			resizeMode='contain'
			onError={() => setFailed(true)}
		/>
	);
};

const thumbnailArtwork =
	(fileId: string, renderFallback: (size: number) => React.ReactNode) => (size: number) => (
		<ThumbnailArtwork fileId={fileId} size={size} renderFallback={renderFallback} />
	);

const pickReward = (rewards: SvaraMissionRewardDto[]): SvaraMissionRewardDto | null => {
	const chips = rewards.find(reward => reward.rewardType === 'Chips');
	if (chips) return chips;

	return rewards.find(reward => reward.rewardType === 'Experience') ?? null;
};

export const toDailyMissionView = (
	dto: SvaraDailyMissionDto,
	options: { busy: boolean },
): DailyMission => {
	const reward = pickReward(dto.rewards ?? []);
	const thumbnailId = dto.thumbnailMobileId ?? dto.thumbnailId;
	const bundledArtwork = ARTWORK[dto.eventType] ?? cardsArtwork;

	return {
		id: dto.id,
		title: dto.title,
		progress: dto.currentCount,
		goal: dto.completionCount,
		progressLabel: `${groupThousands(dto.currentCount)}/${groupThousands(dto.completionCount)}`,
		rewardLabel: reward ? groupThousands(reward.amount) : null,
		rewardIcon: reward ? (reward.rewardType === 'Chips' ? 'chips' : 'experience') : null,
		claimable: dto.completed && !dto.claimed,
		claimed: dto.claimed,
		busy: options.busy,
		renderArtwork: thumbnailId ? thumbnailArtwork(thumbnailId, bundledArtwork) : bundledArtwork,
	};
};

const MISSION_TEXT: Record<string, string> = {
	T_SITE_MISSIONS_CLAIMED: 'CLAIMED',
	T_SITE_MISSIONS_REWARD_PARTIAL: 'Part of your reward could not be paid. Please contact support.',
	T_SITE_MISSIONS_ERROR_NOT_COMPLETED: 'Finish the mission first.',
	T_SITE_MISSIONS_ERROR_EXPIRED: 'These missions have already reset.',
	T_SITE_MISSIONS_ERROR_UNAVAILABLE: 'This reward is not available. Please contact support.',
	T_SITE_MISSIONS_ERROR_FAILED: 'Could not claim your reward. Please try again.',
	T_SITE_MISSIONS_ERROR_LOAD: 'Missions could not be loaded. Please try again.',
};

export const missionText = (t: TFunction, key: string) =>
	t(key, { defaultValue: MISSION_TEXT[key] ?? key });
